import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useUser } from '@/contexts/UserContext';
import { masksByCountry } from '@/helpers/mask';
import urlPrefix from '@/helpers/url-prefix';
import { useUsers } from '@/hooks/useUsers';
import { reloadCards } from '@/store/ducks/cards';
import { showToast } from '@/store/ducks/toasts';

import UserFormComponent from './UserFormComponent';

const UserFormContainer = ({ action }) => {
  const { id } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const me = useUser();
  const country = me?.profile?.country || 'BR';
  const cpfMask = masksByCountry[country].CPF_MASK;
  const phoneMask = masksByCountry[country].PHONE_MASK;
  const cellphoneMask = masksByCountry[country].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[country].CELLPHONE_MASK_LENGTH;

  const { useUserDetails, useFeaturesList, useCreateUserMutation, useUpdateUserMutation } = useUsers();

  const isEditMode = action === 'edit';

  const { data: userData, isLoading: isUserBusy } = useUserDetails(id, {
    enabled: isEditMode,
  });

  const { data: featuresData, isLoading: isFeaturesBusy } = useFeaturesList({
    enabled: !isEditMode,
  });

  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation(id);

  const features = useMemo(() => {
    if (isEditMode) {
      return userData?.features || [];
    }
    return featuresData || [];
  }, [isEditMode, userData, featuresData]);

  const initialValues = useMemo(() => {
    if (isEditMode && userData) {
      return {
        name: userData.name || '',
        cpf: userData.cpf || '',
        phone: userData.phone || '',
        username: userData.username || '',
        email: userData.email || '',
        features: userData.features
          ?.filter(feature => feature?.selected)
          .map(feature => `${feature?.id}`) || [],
      };
    }
    return {
      name: '',
      cpf: '',
      phone: '',
      username: '',
      email: '',
      features: featuresData?.map(feature => `${feature?.id}`) || [],
    };
  }, [isEditMode, userData, featuresData]);

  const isLoading = isEditMode ? isUserBusy : isFeaturesBusy;

  const handleSubmit = ({ name, cpf, phone, email, features: permissions, username }) => {
    if (!permissions.length) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'SELECT_AT_LEAST_ONE_PERMISSION_MESSAGE' }),
          duration: 5000,
        })
      );
      return;
    }

    const payload = { name, cpf, phone, features: permissions };

    if (isEditMode) {
      updateUserMutation.mutate(payload, {
        onSuccess: () => {
          navigate(`${urlPrefix('users')}/${id}`);
          dispatch(
            showToast({
              type: 'success',
              title: intl.formatMessage({ id: 'SUCCESS' }),
              text: intl.formatMessage({ id: 'USER_UPDATE_SUCCESS' }),
              duration: 5000,
            })
          );
        },
        onError: (err) => {
          let message;
          if (err.response?.data) {
            const firstKey = Object.keys(err.response.data)[0];
            message = intl.formatMessage({ id: `${firstKey}.${err.response.data[firstKey]}` });
          } else {
            message = intl.formatMessage({ id: 'DATA_SAVE_ERROR' });
          }
          dispatch(
            showToast({
              type: 'error',
              title: intl.formatMessage({ id: 'ERROR' }),
              text: message,
              duration: 5000,
            })
          );
        },
      });
    } else {
      createUserMutation.mutate(
        { ...payload, email, username },
        {
          onSuccess: () => {
            navigate(urlPrefix('users'));
            dispatch(reloadCards());
            dispatch(
              showToast({
                type: 'success',
                title: intl.formatMessage({ id: 'SUCCESS' }),
                text: intl.formatMessage({ id: 'USER_REGISTRATION_FINISHED_SUCCESS' }),
                duration: 5000,
              })
            );
          },
          onError: (err) => {
            let message;
            if (err.response?.data) {
              const firstKey = Object.keys(err.response.data)[0];
              message = intl.formatMessage({ id: `${firstKey}.${err.response.data[firstKey]}` });
            } else {
              message = intl.formatMessage({ id: 'DATA_SAVE_ERROR' });
            }
            dispatch(
              showToast({
                type: 'error',
                title: intl.formatMessage({ id: 'ERROR' }),
                text: message,
                duration: 5000,
              })
            );
          },
        }
      );
    }
  };

  return (
    <UserFormComponent
      userId={id}
      features={features}
      initialValues={initialValues}
      isUserBusy={isEditMode && isUserBusy}
      isFeaturesBusy={isLoading}
      action={action}
      intl={intl}
      handleSubmit={handleSubmit}
      cpfMask={cpfMask}
      phoneMask={phoneMask}
      cellphoneMask={cellphoneMask}
      cellphoneMaskLength={cellphoneMaskLength}
    />
  );
};

UserFormContainer.propTypes = {
  action: PropTypes.string.isRequired,
};

export default UserFormContainer;
